import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Icon } from '@iconify/react';
import { Button, Chip } from '@nextui-org/react';

interface TabFormData {
  id: string;
  label: string;
  tag: string;
  priority: number;
  is_active: boolean;
}

interface SortableTabCardProps {
  tab: TabFormData;
  index: number;
  onEdit: (tab: TabFormData) => void;
  onDelete: (id: string) => void;
}

const cardColors = [
  '#F0F9FF', // blue-50
  '#ECFDF5', // green-50
  '#FEF3C7', // yellow-50
  '#FDF2F8', // pink-50
  '#F3E8FF', // purple-50
  '#ECFCCB', // lime-50
];

let lastColor = '';

const getRandomColor = () => {
  let randomColor;
  do {
    randomColor = cardColors[Math.floor(Math.random() * cardColors.length)];
  } while (randomColor === lastColor);
  lastColor = randomColor;
  return randomColor;
};

const SortableTabCard = ({
  tab,
  index,
  onEdit,
  onDelete,
}: SortableTabCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const bgColor = getRandomColor();

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div
        className={`group rounded-lg p-4 cursor-grab h-[140px] flex flex-col border border-default-200 ${
          isDragging ? 'shadow-lg ring-2 ring-primary' : ''
        }`}
        style={{ backgroundColor: bgColor }}
        {...listeners}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <Icon
                icon="heroicons:bars-3"
                className="text-default-600 w-4 h-4"
              />
            </div>
            <span className="text-xs font-medium text-default-600">
              #{index + 1}
            </span>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              isIconOnly
              size="sm"
              variant="light"
              className="bg-white/60 min-w-unit-6 w-6 h-6"
              onPress={(e) => {
                e.stopPropagation();
                onEdit(tab);
              }}
            >
              <Icon
                icon="heroicons:pencil"
                className="text-default-600 w-3 h-3"
              />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="light"
              color="danger"
              className="bg-white/60 min-w-unit-6 w-6 h-6"
              onPress={(e) => {
                e.stopPropagation();
                onDelete(tab.id);
              }}
            >
              <Icon icon="heroicons:trash" className="text-danger w-3 h-3" />
            </Button>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-between">
          <div>
            <h4 className="text-sm font-semibold text-default-800 mb-1 truncate">
              {tab.label}
            </h4>
            <p className="text-xs text-default-600 mb-2">{tab.id}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1">
              <Icon icon="heroicons:tag" className="text-default-500 w-3 h-3" />
              <span className="text-xs text-default-600 truncate">
                {tab.tag}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <Chip
                size="sm"
                variant="flat"
                color={tab.is_active ? 'success' : 'default'}
                className="text-xs"
              >
                {tab.is_active ? 'Active' : 'Inactive'}
              </Chip>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

SortableTabCard.displayName = 'SortableTabCard';

export default SortableTabCard;
